import { InputBase } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import FilterNone from '@material-ui/icons/FilterNone';
import produce from 'immer';
import L, {
	LatLng,
	LatLngExpression,
	LeafletKeyboardEvent,
	Point
} from 'leaflet';
import { Map } from 'leaflet';
import {
	ChangeEvent,
	createRef,
	ReactElement,
	RefObject,
	useEffect,
	MouseEvent,
	TouchEvent,
	useState,
	KeyboardEvent,
	DragEvent,
	useCallback,
	useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { Popup, useMap } from 'react-leaflet';
import { connect } from 'react-redux';

import { Body1, LeafletDivLayer, TooltipIconButton } from '..';
import { useCallbackSafeState, usePrevious } from '../../../hooks';
import {
	AppState,
	AppThunkDispatch,
	IMapDrawing,
	IText,
	ITextMapDrawing,
	MapViewerPanel,
	ProjectActions,
	projectAddDrawing,
	projectCloseDrawingPopup,
	projectEditDrawingColor,
	projectEditDrawingPopup,
	projectEditDrawingText,
	projectEditDrawingWeight,
	projectMoveDrawing,
	projectRemoveDrawing
} from '../../../redux';
import {
	createNewDrawingId,
	disablePanning,
	enablePanning,
	isAppleDevice,
	RectangleOverwriteLayer
} from '../../../utils';
import { ColorPicker } from '../../molecules';

import './map-text-shape.styles.scss';

export interface IMapTextShapeProps {
	drawing: ITextMapDrawing;
}

export interface IMapTextShapeStateProps {
	disabled: boolean;
	removeDrawing: (drawing: IMapDrawing) => void;
	addDrawing: (drawing: IMapDrawing) => void;
	editDrawingColor: (drawing: IMapDrawing) => void;
	editDrawingPopup: (drawing: IMapDrawing) => void;
	editDrawingWeight: (drawing: IMapDrawing) => void;
	editDrawingText: (drawing: IMapDrawing) => void;
	editDrawingPosition: (drawing: IMapDrawing) => void;
	closeDrawingPopup: () => void;
}

function MapTextShapeTextView(
	props: IMapTextShapeProps & IMapTextShapeStateProps
): ReactElement {
	const {
		drawing,
		disabled,
		removeDrawing,
		addDrawing,
		editDrawingColor,
		editDrawingPopup,
		editDrawingWeight,
		editDrawingText,
		editDrawingPosition,
		closeDrawingPopup
	} = props;
	const { shape } = drawing;
	const map: Map = useMap();
	const { t } = useTranslation('map');

	const textElementRef: RefObject<HTMLParagraphElement> =
		createRef<HTMLParagraphElement>();
	const [getTextElement, setTextElement] =
		useCallbackSafeState<HTMLParagraphElement | null>(
			textElementRef.current
		);
	const containerRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
	const totalContainerRef = useRef<HTMLDivElement | null>(null);
	const [layer, setLayer] = useState<L.Rectangle>();
	const previousLayer: L.Rectangle | undefined =
		usePrevious<L.Rectangle | undefined>(layer);
	const previousIsOpen: boolean | undefined = usePrevious<boolean>(
		drawing.isPopupOpen
	);
	const [getIsDragging, setIsDragging] = useCallbackSafeState<boolean>(false);
	const [getTempPosition, setTempPosition] = useCallbackSafeState<LatLng>(
		shape.position
	);

	const localStorageKey: string = 'drag-state';

	const duplicateShapeOffset: Point = new Point(30, 30);

	useEffect(() => {
		window.addEventListener('click', closePopupWhenClickedOutside);
		return () => {
			window.removeEventListener('click', closePopupWhenClickedOutside);
		};
	}, []);

	function closePopupWhenClickedOutside(e: Event) {
		if (!drawing.isPopupOpen) return;
		if (!(e.target as Element).contains(totalContainerRef.current)) {
			closeDrawingPopup();
		}
	}

	useEffect(() => {
		document.addEventListener('dragover', handleDrag);
		document.addEventListener('touchmove', handleDrag);

		return () => {
			document.removeEventListener('dragover', handleDrag);
			document.removeEventListener('touchmove', handleDrag);
		};
	}, []);

	useEffect(() => {
		const element: HTMLParagraphElement | null = textElementRef.current;
		if (element) {
			element.removeEventListener(
				'touchstart',
				(event: globalThis.TouchEvent) => {
					event.stopPropagation();
				}
			);
			element.addEventListener(
				'touchstart',
				(event: globalThis.TouchEvent) => {
					event.stopPropagation();
				}
			);
		}
		setTextElement(element);
	}, [textElementRef]);

	useEffect(() => {
		if (!drawing.isPopupOpen && previousIsOpen) {
			saveDrawingText();
		}
		if (!previousIsOpen && drawing.isPopupOpen) {
			handleOnEnter();
		}
		if (!drawing.isPopupOpen) {
			enablePanning(map);
		}

		setupShortcuts();

		return removeShortcuts;
	}, [drawing.isPopupOpen]);

	useEffect(() => {
		evaluatePopupState();
	}, [drawing.isPopupOpen, disabled]);

	useEffect(() => {
		if (!layer) return;
		if (layer && !previousLayer && drawing.isPopupOpen) {
			if (containerRef.current) {
				containerRef.current.focus();
			}
			openPopup();
		}
	}, [layer]);

	const calculatePopupPosition = useCallback<() => LatLngExpression>(() => {
		const centerPoint: Point = (
			layer as any
		).editing._getCenterPoint() as Point;
		const corner1: Point = map.latLngToLayerPoint(
			(layer as RectangleOverwriteLayer)._corner1
		);

		const point: Point = new Point(centerPoint.x, corner1.y);
		const coord: LatLng = map.layerPointToLatLng(point);
		return [coord.lat, coord.lng];
	}, [layer]);

	function setupShortcuts(): void {
		map.on('keydown', handleKeyPress);
	}

	function removeShortcuts(): void {
		map.off('keydown', handleKeyPress);
	}

	function handleKeyPress(event: LeafletKeyboardEvent): void {
		if (!drawing.isPopupOpen) return;

		const actionDuplicate: boolean =
			(event.originalEvent.ctrlKey ||
				(isAppleDevice() && event.originalEvent.metaKey)) &&
			event.originalEvent.key === 'd';
		const actionDelete: boolean = event.originalEvent.key === 'Delete';

		if (actionDuplicate) {
			handleDuplicateDrawing();
			event.originalEvent.preventDefault();
		}

		if (actionDelete) {
			handleRemoveDrawing();
			event.originalEvent.preventDefault();
		}
	}

	function evaluatePopupState(): void {
		if (drawing.isPopupOpen) {
			if (layer && !layer.isPopupOpen() && containerRef.current) {
				containerRef.current.focus();
			}
			openPopup();
		}
		if (!drawing.isPopupOpen || disabled) {
			closePopup();
		}
	}

	function openPopup(): void {
		if (!layer || !drawing.isPopupOpen || getIsDragging()) return;

		setTimeout(() => {
			layer.openPopup(calculatePopupPosition());
		}, 0);
	}

	function closePopup(): void {
		if (!layer) return;

		setTimeout(() => {
			layer.closePopup();
		}, 0);
	}

	function requestClose(): void {
		closeDrawingPopup();
	}

	function changeColor(color: string): void {
		const newDrawing: IMapDrawing = produce(
			drawing,
			(draft: IMapDrawing) => {
				draft.shape.color = color;
			}
		);
		editDrawingColor(newDrawing);
	}

	function changeFontSize(event: ChangeEvent<HTMLInputElement>): void {
		let value: number = event.target.valueAsNumber;

		const newDrawing: IMapDrawing = produce(
			drawing,
			(draft: IMapDrawing) => {
				(draft.shape as IText).fontSize = value;
			}
		);
		editDrawingWeight(newDrawing);
		openPopup();
	}

	function handleRemoveDrawing(): void {
		removeDrawing(drawing);
	}

	function handleDuplicateDrawing(): void {
		const newText: string =
			getTextElement() !== null
				? getTextElement()!.innerHTML
				: shape.text;

		const duplicate: ITextMapDrawing = {
			id: createNewDrawingId(),
			isPopupOpen: true,
			shape: {
				...shape,
				text: newText,
				position: offsetPoint(shape.position)
			}
		};
		addDrawing(duplicate);
		closePopup();
	}

	function offsetPoint(coord: LatLng): LatLng {
		const point: Point = map.project(coord);
		return map.unproject(point.add(duplicateShapeOffset));
	}

	function saveDrawingText(): void {
		if (!textElementRef.current || getIsDragging()) return;

		const text: string = textElementRef.current.innerHTML;

		const newDrawing: IMapDrawing = produce(
			drawing,
			(draft: IMapDrawing) => {
				(draft.shape as IText).finished = true;
				(draft.shape as IText).text = text;
			}
		);
		editDrawingText(newDrawing);
	}

	function handleLayerClick(event: MouseEvent<HTMLDivElement>): void {
		if (disabled) return;
		L.DomEvent.stop(event.nativeEvent);

		const newDrawing: IMapDrawing = produce(
			drawing,
			(draft: IMapDrawing) => {
				draft.isPopupOpen = true;
			}
		);
		editDrawingPopup(newDrawing);
	}

	function handleOnEnter(): void {
		if (drawing.isPopupOpen) {
			disablePanning(map);
			if (!containerRef.current) return;
			L.DomEvent.disableClickPropagation(containerRef.current);
		}
	}

	function handleOnLeave(): void {
		enablePanning(map);
	}

	function insertNewLine(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			document.execCommand('insertHTML', false, '\n');
			event.preventDefault();
		}
	}

	function startDrag(
		event: DragEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
	): void {
		const { clientX, clientY } = getClientCoordsFromEvent(event);

		if ('dataTransfer' in event) {
			const dragEvent: DragEvent = event as DragEvent<HTMLDivElement>;
			dragEvent.dataTransfer.setDragImage(
				document.createElement('div'),
				0,
				0
			);
		}

		setIsDragging(true);
		setTempPosition(shape.position);
		closePopup();

		localStorage.setItem(
			localStorageKey,
			JSON.stringify({ x: clientX, y: clientY })
		);
	}

	function handleDrag(
		event: globalThis.MouseEvent | globalThis.TouchEvent
	): void {
		if (!getIsDragging()) return;

		const { clientX, clientY } = getClientCoordsFromEvent(event);

		const { x, y } = JSON.parse(
			localStorage.getItem(localStorageKey) as string
		);

		changeLocation(x, y, clientX, clientY);

		localStorage.setItem(
			localStorageKey,
			JSON.stringify({ x: clientX, y: clientY })
		);
	}

	function changeLocation(
		x: number,
		y: number,
		newX: number,
		newY: number
	): void {
		const currentPosition: Point = map.project(getTempPosition());
		const delta: Point = new Point(newX - x, newY - y);

		if ((newX === 0 && newY === 0) || delta.distanceTo([0, 0]) === 0)
			return;

		const newPoint: Point = currentPosition.add(delta);

		setTempPosition(map.unproject(newPoint));
	}

	function handleEndDrag(): void {
		const newDrawing: ITextMapDrawing = produce(
			drawing,
			(draft: ITextMapDrawing) => {
				draft.isPopupOpen = true;
				draft.shape.position = getTempPosition();
			}
		);

		openPopup();
		editDrawingPosition(newDrawing);
		setIsDragging(false);
	}

	function handleOnFocus(): void {
		if (containerRef.current) {
			window.getSelection()?.selectAllChildren(containerRef.current);
		}
	}

	function getClientCoordsFromEvent(
		event:
			| DragEvent<HTMLDivElement>
			| globalThis.MouseEvent
			| TouchEvent<HTMLDivElement>
			| globalThis.TouchEvent
	): { clientX: number; clientY: number } {
		let clientX: number = 0;
		let clientY: number = 0;

		if ('clientX' in event && 'clientY' in event) {
			const mouseEvent: DragEvent | globalThis.MouseEvent = event as
				| DragEvent<HTMLDivElement>
				| globalThis.MouseEvent;
			clientX = mouseEvent.clientX;
			clientY = mouseEvent.clientY;
		}

		if ('touches' in event) {
			const touchEvent: TouchEvent | globalThis.TouchEvent = event as
				| TouchEvent<HTMLDivElement>
				| globalThis.TouchEvent;
			clientX = touchEvent.touches[0].clientX;
			clientY = touchEvent.touches[0].clientY;
		}

		return { clientX, clientY };
	}

	return (
		<LeafletDivLayer
			onRef={setLayer}
			center={getIsDragging() ? getTempPosition() : shape.position}
			className="map-control-container map-text-shape"
			onClick={handleLayerClick}
			onResize={openPopup}
		>
			<div ref={totalContainerRef}>
				{layer && (
					<Popup
						closeButton={false}
						autoClose={false}
						className="map-text-shape-popup"
						onOpen={openPopup}
						onClose={requestClose}
					>
						<PopupInputElement
							shape={shape}
							changeFontSize={changeFontSize}
						/>
						<TooltipIconButton
							onClick={() => {}}
							tooltip={t('actionButtons.edit.text.color')}
							placement="top"
							icon={
								<ColorPicker
									color={shape.color}
									onChange={changeColor}
								/>
							}
						/>
						{shape.finished && (
							<>
								<TooltipIconButton
									onClick={handleDuplicateDrawing}
									tooltip={t(
										'actionButtons.edit.text.duplicate'
									)}
									placement="top"
									icon={<FilterNone />}
								/>
								<TooltipIconButton
									onClick={handleRemoveDrawing}
									tooltip={t(
										'actionButtons.edit.text.delete'
									)}
									placement="top"
									icon={<Delete htmlColor="#CF0101" />}
								/>
							</>
						)}
					</Popup>
				)}
				<div
					onMouseEnter={handleOnEnter}
					onMouseLeave={handleOnLeave}
					onDragStart={startDrag}
					onTouchStart={startDrag}
					onDragEnd={handleEndDrag}
					onTouchEnd={handleEndDrag}
					draggable={drawing.isPopupOpen}
					className={`map-text-shape-container ${
						drawing.isPopupOpen ? 'selected' : ''
					} ${getIsDragging() ? 'dragging' : ''}`}
					contentEditable={drawing.isPopupOpen}
					suppressContentEditableWarning
					onKeyDown={insertNewLine}
					onFocus={handleOnFocus}
					ref={containerRef}
				>
					<Body1
						style={{
							fontSize: `${shape.fontSize / 10}rem`,
							width: '100%',
							height: '100%',
							lineHeight: `${shape.fontSize / 10}rem`,
							color: shape.color
						}}
						ref={textElementRef}
					>
						{shape.text}
					</Body1>
				</div>
			</div>
		</LeafletDivLayer>
	);
}

function PopupInputElement({ shape, changeFontSize }: any): ReactElement {
	return (
		<InputBase
			type="number"
			value={Number.isNaN(shape.fontSize) ? '' : shape.fontSize}
			onChange={changeFontSize}
		/>
	);
}

function mapStateToProps(state: AppState) {
	return {
		disabled: !(
			state.mapViewer.panels.get(MapViewerPanel.Selection) || false
		)
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProjectActions>) {
	return {
		removeDrawing: (drawing: IMapDrawing) =>
			dispatch(projectRemoveDrawing(drawing)),
		addDrawing: (drawing: IMapDrawing) =>
			dispatch(projectAddDrawing(drawing)),
		editDrawingColor: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingColor(drawing)),
		editDrawingPopup: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingPopup(drawing)),
		closeDrawingPopup: () => dispatch(projectCloseDrawingPopup()),
		editDrawingWeight: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingWeight(drawing)),
		editDrawingText: (drawing: IMapDrawing) =>
			dispatch(projectEditDrawingText(drawing)),
		editDrawingPosition: (drawing: IMapDrawing) =>
			dispatch(projectMoveDrawing(drawing))
	};
}

export const MapTextShape = connect(
	mapStateToProps,
	mapDispatchToProps
)(MapTextShapeTextView);
